import React from 'react';
import {
  DataGrid,
} from '@mui/x-data-grid';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Collapse,
  CircularProgress,
  Pagination,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import CompanyBillingSummaryModal from 'Components/CompanyBillingSummaryModal';
import CoordinatorWiseSummaryModal from 'Components/CoordinatorWiseSummaryModal';
import { loadBillingFilters, saveBillingFilters } from 'Includes/billingFilterStorage';
import OrdersSourceToggle, { loadOrdersSource } from 'Components/OrdersSourceToggle';
import { TEAM_STATUS_CHOICES } from '../../Includes/teamStatus';
import { useNavigate } from 'react-router-dom';
import ReportsMobileBottomNav from 'Components/ReportsMobileBottomNav';

function CompanyFinanceSummaryList(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const storedFilters = loadBillingFilters();
  const navigate = useNavigate();

  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all');
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = React.useState('');
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [selectedCompanies, setSelectedCompanies] = React.useState([]);
  const [serviceList, setServiceList] = React.useState([]);
  const [selectedService, setSelectedService] = React.useState(null);
  const [paidCompare, setPaidCompare] = React.useState('');
  const [paidPercent, setPaidPercent] = React.useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  const [users, setUsers] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [sortModel] = React.useState([
    { field: 'balance_to_be_paid_by_today', sort: 'desc' }
  ]);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [rowCount, setRowCount] = React.useState(0);
  const [overallSummaryData, setOverallSummaryData] = React.useState(null);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailData, setDetailData] = React.useState(null);
  const [detailIndex, setDetailIndex] = React.useState(-1);
  const [coordinatorSummaryOpen, setCoordinatorSummaryOpen] = React.useState(false);
  const [coordinatorSummaryData] = React.useState([]);
  const [coordinatorSummaryLoading] = React.useState(false);
  const [orderSource, setOrderSource] = React.useState(loadOrdersSource());

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const overallSummary = React.useMemo(() => {
    if (!overallSummaryData) {
      return {
        totals: { order: 0, issued: 0, invoiced: 0, paid: 0, balance: 0, discount: 0, dueByToday: 0, balanceToPayByToday: 0 },
        counts: {}
      };
    }
    return overallSummaryData;
  }, [overallSummaryData]);

  React.useEffect(() => {
    getAcademicYearList();
    getUsers();
    getServiceList();
    getCompanyOptions();
  }, []);

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then((response) => {
      if (response?.status === 200) {
        const list = response.data.data || [];
        setAcademicYearList(list);

        if (selectedAcademicYear) {
          fetchSummary(selectedAcademicYear);
        }
      }
    });
  };

  const getServiceList = () => {
    getRequest(GET_URL.services?.api, {}, props).then((response) => {
      if (response?.status === 200) {
        setServiceList(response.data.data || []);
      }
    });
  };

  const getCompanyOptions = () => {
    getRequest(GET_URL.company.api, { limit: 10000, pageno: 1 }, props).then((response) => {
      if (response?.status === 200) {
        const raw = response.data.data || response.data || [];
        setCompanyOptions(Array.isArray(raw) ? raw : []);
      }
    });
  };

  const paidPercentParams = () => {
    const percent = String(paidPercent ?? '').trim();
    if (!paidCompare || percent === '') return {};
    return { paid_compare: paidCompare, paid_percent: percent };
  };

  const companyParams = () => {
    if (selectedCompanies.length) {
      return { company: selectedCompanies.map((c) => c.id).join(',') };
    }
    if (debouncedSearch) {
      return { company: debouncedSearch };
    }
    return {};
  };

  const getUsers = () => {
    getRequest(GET_URL.user.api, { groups: '4' }, props).then((response) => {
      if (response?.status === 200) {
        setUsers(response.data.data || []);
      }
    });
  };

  const fetchSummary = (yearId) => {
    if (!yearId) return;

    setLoading(true);

    const params = {
      year: yearId,
      limit: pageSize,
      pageno: page + 1,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
      ...(coordinatorFilter && { coordinator: coordinatorFilter }),
      ...companyParams(),
      ...(selectedService?.id && { service: selectedService.id }),
      ...paidPercentParams(),
      order_source: orderSource,
      ...(sortModel?.length && {
        ordering: sortModel[0].sort === 'desc'
          ? `-${sortModel[0].field}`
          : sortModel[0].field
      })
    };

    getRequest(GET_URL.billing_config.api, { ...params, finance_summary: 1 }, props)
      .then((response) => {
        setLoading(false);
        if (response?.status === 200) {
          setRows(response.data?.data || []);
          setRowCount(response.data?.count || 0);
          setOverallSummaryData(response.data?.summary || null);
        }
      })
      .catch(() => setLoading(false));
  };

  React.useEffect(() => {
    if (selectedAcademicYear) {
      fetchSummary(selectedAcademicYear);
    }
  }, [
    selectedAcademicYear,
    statusByTeamFilter,
    clientBillingStatusFilter,
    coordinatorFilter,
    selectedCompanies,
    debouncedSearch,
    selectedService,
    paidCompare,
    paidPercent,
    sortModel,
    page,
    pageSize,
    orderSource,
  ]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setStatusByTeamFilter('all');
    setClientBillingStatusFilter('');
    setCoordinatorFilter('');
    setSelectedCompanies([]);
    setSelectedService(null);
    setPaidCompare('');
    setPaidPercent('');
    setPage(0);
  };

  const activeFiltersCount = [
    searchQuery,
    statusByTeamFilter !== 'all',
    clientBillingStatusFilter,
    coordinatorFilter,
    selectedCompanies.length > 0,
    selectedService,
    paidCompare,
  ].filter(Boolean).length;

  const openDetailsByIndex = (idx) => {
    if (idx < 0 || idx >= rows.length) return;
    const row = rows[idx];
    setDetailIndex(idx);
    if (orderSource === 'orders_v2' || row?.source === 'orders_v2') {
      const orderId = row.order_v2_id || row.config_id || row.id;
      if (orderId) {
        navigate(`/billing/orders-v2/form?id=${orderId}`);
      }
      return;
    }
    setDetailOpen(true);
    setDetailData(null);

    getRequest(`${GET_URL.billing_config.api}${row.config_id}/`, {}, props)
      .then(res => setDetailData(res?.data || null));
  };

  const openDetails = (row) => {
    const idx = rows.findIndex(r => r.id === row.id);
    openDetailsByIndex(idx >= 0 ? idx : 0);
  };

  const handleDownloadExcel = () => {
    if (!selectedAcademicYear) return;
    getRequest(
      GET_URL.billing_config.api,
      {
        year: selectedAcademicYear,
        ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
        ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...companyParams(),
        ...(selectedService?.id && { service: selectedService.id }),
        ...paidPercentParams(),
        finance_summary_export: 1,
        order_source: orderSource,
      },
      { ...props, responseType: 'blob' }
    )
      .then((response) => {
        if (response && response.status === 200) {
          const contentType =
            response?.headers?.['content-type'] ||
            response?.headers?.['Content-Type'] ||
            'application/octet-stream';
          const responseBlob = response?.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(responseBlob);
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
          let filename = `companies_billing_summary_year_${selectedAcademicYear}.xlsx`;
          if (typeof contentDisposition === 'string') {
            const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            const asciiMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
            const extracted = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : asciiMatch?.[1];
            if (extracted) filename = extracted;
          }
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(link);
        }
      });
  };

  const getStatusColor = (status) => {
    switch (String(status).toLowerCase()) {
      case 'paid':
        return { bg: '#e8f5e9', text: '#2e7d32', label: 'Paid' };
      case 'partially_paid':
        return { bg: '#fff3e0', text: '#ef6c00', label: 'Partial' };
      case 'unpaid':
        return { bg: '#ffebee', text: '#c62828', label: 'Unpaid' };
      case 'no_invoice':
        return { bg: '#f5f5f5', text: '#616161', label: 'No Invoice' };
      default:
        return { bg: '#ede7f6', text: '#512da8', label: status || '-' };
    }
  };

  const columns = [
    {
      field: 'company_name',
      headerName: 'Company Name',
      minWidth: 240,
      flex: 1.3,
      renderCell: (params) => {
        const balance = parseFloat(params.row.balance_amount || 0);
        const due = parseFloat(params.row.balance_to_be_paid_by_today || 0);
        const color = due > 0 ? '#b91c1c' : balance <= 0 ? '#15803d' : '#0f172a';

        return (
          <Box sx={{ py: 0.8, cursor: 'pointer' }}>
            <Typography variant="body2" fontWeight={800} color={color} noWrap sx={{ '&:hover': { textDecoration: 'underline' } }}>
              {params.value || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.company_id || params.row.id} • {params.row.status_by_team || 'Active'}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'coordinator_name',
      headerName: 'Coordinator',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color="#334155" fontWeight={600}>
          {params.value || <span style={{ color: '#94a3b8' }}>Unassigned</span>}
        </Typography>
      )
    },
    {
      field: 'order_total_amount',
      headerName: 'Order / Invoiced',
      width: 160,
      renderCell: (params) => {
        const invoiced = parseFloat(params.row.issued_invoice_total_amount || 0);
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" fontWeight={800} color="#0f172a">
              {formatAmount(params.value)}
            </Typography>
            <Typography variant="caption" color="#64748b" display="block">
              Invoiced: {formatAmount(invoiced)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'paid_total_amount',
      headerName: 'Collection (Paid)',
      width: 165,
      renderCell: (params) => {
        const paidVal = parseFloat(params.value || 0);
        const paidPct = Math.min(100, Math.max(0, parseFloat(params.row.paid_percentage || 0)));
        return (
          <Box sx={{ width: '100%', py: 0.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={800} color="#15803d">
                {formatAmount(paidVal)}
              </Typography>
              <Typography variant="caption" fontWeight={700} color="#15803d" fontSize="0.7rem">
                {paidPct.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={paidPct}
              sx={{
                height: 5,
                borderRadius: 2.5,
                backgroundColor: '#e2e8f0',
                mt: 0.4,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2.5,
                  backgroundColor: paidPct >= 100 ? '#16a34a' : paidPct > 0 ? '#3b82f6' : '#94a3b8',
                },
              }}
            />
          </Box>
        );
      }
    },
    {
      field: 'balance_amount',
      headerName: 'Pending / Due',
      width: 165,
      renderCell: (params) => {
        const balance = parseFloat(params.row.balance_amount || 0);
        const due = parseFloat(params.row.balance_to_be_paid_by_today || 0);
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" fontWeight={800} color={balance > 0 ? '#c2410c' : '#15803d'}>
              {formatAmount(balance)}
            </Typography>
            {due > 0 ? (
              <Typography variant="caption" fontWeight={700} color="#dc2626" display="block">
                ⚠️ Due Today: {formatAmount(due)}
              </Typography>
            ) : (
              <Typography variant="caption" color="#94a3b8" display="block">
                No due today
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'payment_status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const st = getStatusColor(params.value);
        return (
          <Chip
            size="small"
            label={st.label}
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              backgroundColor: st.bg,
              color: st.text,
            }}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 115,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={(e) => {
            e.stopPropagation();
            openDetails(params.row);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.72rem',
            py: 0.3,
            px: 1,
            borderRadius: 1.5,
            borderColor: '#cbd5e1',
            color: '#334155',
            '&:hover': {
              borderColor: '#0284c7',
              backgroundColor: '#f0f9ff',
              color: '#0284c7',
            },
          }}
        >
          Details
        </Button>
      )
    }
  ];

  const orderTotal = parseFloat(overallSummary.totals.order || 0);
  const paidTotal = parseFloat(overallSummary.totals.paid || 0);
  const pendingTotal = parseFloat(overallSummary.totals.balance || 0);
  const dueTodayTotal = parseFloat(overallSummary.totals.balanceToPayByToday || 0);
  const issuedTotal = parseFloat(overallSummary.totals.issued || 0);
  const overallPaidPercent = orderTotal ? `${((paidTotal / orderTotal) * 100).toFixed(1)}%` : '0.0%';

  return (
    <Box p={{ xs: 1.5, sm: 2.5 }} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* TOP HEADER */}
      <Box mb={2}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              Billing Summary
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Company financial overview, collection progress, and due balances
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <OrdersSourceToggle
              value={orderSource}
              onChange={(next) => {
                setOrderSource(next);
                setPage(0);
              }}
            />
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownloadExcel}
              disabled={!selectedAcademicYear || loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: '#cbd5e1',
                color: '#334155',
                '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' }
              }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* EXECUTIVE KPI SUMMARY CARDS */}
      {selectedAcademicYear && (
        <Grid container spacing={1.5} mb={2}>
          {/* Companies Count */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <BusinessOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.65rem">
                  Clients
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a">
                {rowCount.toLocaleString('en-IN')}
              </Typography>
            </Paper>
          </Grid>

          {/* Total Orders */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                <Typography variant="caption" fontWeight={700} color="#3b82f6" textTransform="uppercase" fontSize="0.65rem">
                  Orders
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
                {formatAmount(orderTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Paid / Collected */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                <Typography variant="caption" fontWeight={700} color="#16a34a" textTransform="uppercase" fontSize="0.65rem">
                  Paid ({overallPaidPercent})
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#15803d" noWrap>
                {formatAmount(paidTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Pending Balance */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #fed7aa', backgroundColor: '#fff7ed' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: '#ea580c' }} />
                <Typography variant="caption" fontWeight={700} color="#ea580c" textTransform="uppercase" fontSize="0.65rem">
                  Pending
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#c2410c" noWrap>
                {formatAmount(pendingTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Due By Today */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: dueTodayTotal > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0', backgroundColor: dueTodayTotal > 0 ? '#fef2f2' : '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <ErrorOutlineOutlinedIcon sx={{ fontSize: 16, color: dueTodayTotal > 0 ? '#dc2626' : '#64748b' }} />
                <Typography variant="caption" fontWeight={700} color={dueTodayTotal > 0 ? '#dc2626' : '#64748b'} textTransform="uppercase" fontSize="0.65rem">
                  Due Today
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color={dueTodayTotal > 0 ? '#b91c1c' : '#0f172a'} noWrap>
                {formatAmount(dueTodayTotal)}
              </Typography>
            </Paper>
          </Grid>

          {/* Status Breakdown Pills */}
          <Grid item xs={6} sm={4} md={2}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Tooltip title="Company payment status breakdown" arrow placement="top">
                <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.65rem" mb={0.5} sx={{ cursor: 'help' }}>
                  Status
                </Typography>
              </Tooltip>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                <Tooltip title={`Paid: ${overallSummary.counts?.paid || 0} companies fully paid`} arrow placement="top">
                  <Chip 
                    size="small" 
                    label={`P: ${overallSummary.counts?.paid || 0}`} 
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, cursor: 'help' }} 
                  />
                </Tooltip>
                <Tooltip title={`Partially Paid: ${overallSummary.counts?.partially_paid || 0} companies with partial payments`} arrow placement="top">
                  <Chip 
                    size="small" 
                    label={`Pt: ${overallSummary.counts?.partially_paid || 0}`} 
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, cursor: 'help' }} 
                  />
                </Tooltip>
                <Tooltip title={`Unpaid: ${overallSummary.counts?.unpaid || 0} companies with zero payments`} arrow placement="top">
                  <Chip 
                    size="small" 
                    label={`U: ${overallSummary.counts?.unpaid || 0}`} 
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, cursor: 'help' }} 
                  />
                </Tooltip>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* STICKY SEARCH & FILTER CONTROLS */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 66, sm: 66 },
          zIndex: 90,
          p: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          {/* Quick Search */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search company name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                }
              }}
            />
          </Grid>

          {/* Academic Year */}
          <Grid item xs={6} sm={3} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value);
                  saveBillingFilters({ academicYearId: e.target.value });
                  setPage(0);
                }}
                label="Academic Year"
                sx={{ borderRadius: 2 }}
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Client Status */}
          <Grid item xs={6} sm={3} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status (Team)</InputLabel>
              <Select
                value={statusByTeamFilter}
                label="Status (Team)"
                onChange={(e) => {
                  setStatusByTeamFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ borderRadius: 2 }}
              >
                {TEAM_STATUS_CHOICES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Actions: More Filters Toggle & Reset */}
          <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              size="small"
              variant={showAdvancedFilters ? 'contained' : 'outlined'}
              startIcon={showAdvancedFilters ? <FilterListOffIcon /> : <FilterListIcon />}
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 1.5,
                bgcolor: showAdvancedFilters ? '#0f172a' : 'transparent',
                borderColor: '#cbd5e1',
                color: showAdvancedFilters ? '#ffffff' : '#334155',
                '&:hover': {
                  bgcolor: showAdvancedFilters ? '#1e293b' : '#f1f5f9',
                  borderColor: '#94a3b8',
                }
              }}
            >
              {showAdvancedFilters ? 'Fewer Filters' : 'More Filters'}
              {activeFiltersCount > 0 && !showAdvancedFilters && (
                <Chip size="small" label={activeFiltersCount} sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
              )}
            </Button>

            {activeFiltersCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={handleResetFilters}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#ef4444' }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>

        {/* ADVANCED FILTER SECTION (COLLAPSIBLE) */}
        <Collapse in={showAdvancedFilters}>
          <Divider sx={{ my: 1.5 }} />
          <Grid container spacing={1.5}>
            {/* Service */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={serviceList}
                getOptionLabel={(o) => o.name || ''}
                value={selectedService}
                onChange={(e, v) => {
                  setSelectedService(v);
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} label="Service Filter" />}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Coordinator */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Coordinator</InputLabel>
                <Select
                  value={coordinatorFilter}
                  label="Coordinator"
                  onChange={(e) => {
                    setCoordinatorFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Coordinators</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.first_name || u.username} {u.last_name || ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Billing Status */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Billing Status</InputLabel>
                <Select
                  value={clientBillingStatusFilter}
                  label="Billing Status"
                  onChange={(e) => {
                    setClientBillingStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="GOOD">GOOD</MenuItem>
                  <MenuItem value="CRITICAL">CRITICAL</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Paid Comparison */}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Paid vs Total</InputLabel>
                <Select
                  value={paidCompare}
                  label="Paid vs Total"
                  onChange={(e) => {
                    setPaidCompare(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Any %</MenuItem>
                  <MenuItem value="gt">Paid &gt;</MenuItem>
                  <MenuItem value="lt">Paid &lt;</MenuItem>
                  <MenuItem value="eq">Paid =</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Paid % */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="% of total"
                value={paidPercent}
                disabled={!paidCompare}
                inputProps={{ min: 0, max: 100, step: '1' }}
                onChange={(e) => {
                  setPaidPercent(e.target.value);
                  setPage(0);
                }}
                placeholder="e.g. 50"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* CONTENT: MOBILE CARD LIST VS DESKTOP TABLE */}
      {isMobile ? (
        <Box>
          {loading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : rows.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Typography color="text.secondary">No company billing records found</Typography>
            </Paper>
          ) : (
            rows.map((row) => {
              const balance = parseFloat(row.balance_amount || 0);
              const due = parseFloat(row.balance_to_be_paid_by_today || 0);
              const paid = parseFloat(row.paid_total_amount || 0);
              const order = parseFloat(row.order_total_amount || 0);
              const paidPct = parseFloat(row.paid_percentage || 0);
              const st = getStatusColor(row.payment_status);

              return (
                <Paper
                  key={row.order_v2_id || row.config_id || row.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2.5,
                    border: due > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  }}
                >
                  {/* Top: Name & Status */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.3 }}>
                        {row.company_name || 'Unnamed Company'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {row.company_id || row.id} • {row.coordinator_name ? `Coordinator: ${row.coordinator_name}` : 'Unassigned'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={st.label}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        backgroundColor: st.bg,
                        color: st.text,
                        height: 22,
                      }}
                    />
                  </Box>

                  {/* Overdue Banner */}
                  {due > 0 && (
                    <Box
                      sx={{
                        mt: 1.2,
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fee2e2',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="#dc2626">
                        ⚠️ Due Today
                      </Typography>
                      <Typography variant="body2" fontWeight={800} color="#b91c1c">
                        {formatAmount(due)}
                      </Typography>
                    </Box>
                  )}

                  {/* Financial Grid */}
                  <Box
                    sx={{
                      mt: due > 0 ? 1 : 1.2,
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#64748b" display="block" fontSize="0.7rem">
                          Order Total
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#1e293b" noWrap>
                          {formatAmount(order)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#16a34a" display="block" fontSize="0.7rem">
                          Paid ({paidPct.toFixed(0)}%)
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#15803d" noWrap>
                          {formatAmount(paid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#ea580c" display="block" fontSize="0.7rem">
                          Pending
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#c2410c" noWrap>
                          {formatAmount(balance)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Footer Actions */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5} pt={1} borderTop="1px solid #f1f5f9">
                    <Typography variant="caption" color="text.secondary">
                      Invoiced: {formatAmount(row.issued_invoice_total_amount)}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openDetails(row)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        py: 0.4,
                        px: 1.2,
                        borderColor: '#cbd5e1',
                        color: '#334155',
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Paper>
              );
            })
          )}

          {/* Mobile Pagination */}
          {rowCount > pageSize && (
            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
              <Pagination
                count={Math.ceil(rowCount / pageSize)}
                page={page + 1}
                onChange={(e, p) => setPage(p - 1)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.order_v2_id || row.config_id || row.id}
            loading={loading}
            autoHeight
            rowHeight={56}
            headerHeight={46}
            onRowClick={(params) => openDetails(params.row)}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            rowCount={rowCount}
            disableSelectionOnClick={false}
            sx={{
              border: 0,
              cursor: 'pointer',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                color: '#475569',
                fontWeight: 700,
                fontSize: '0.82rem',
                borderBottom: '1px solid #e2e8f0',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f1f5f9',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f1f5f9',
              },
            }}
          />
        </Paper>
      )}

      <CompanyBillingSummaryModal
        open={detailOpen}
        loading={detailOpen && !detailData}
        data={detailData}
        onClose={() => {
          setDetailOpen(false);
          setDetailIndex(-1);
        }}
        currentIndex={detailIndex}
        totalCount={rows.length}
        onPrev={detailIndex > 0 ? () => openDetailsByIndex(detailIndex - 1) : null}
        onNext={detailIndex < rows.length - 1 ? () => openDetailsByIndex(detailIndex + 1) : null}
      />

      <CoordinatorWiseSummaryModal
        open={coordinatorSummaryOpen}
        loading={coordinatorSummaryLoading}
        data={coordinatorSummaryData}
        onClose={() => setCoordinatorSummaryOpen(false)}
      />

      {/* Mobile Bottom Navigation for 1-tap switching between the 3 reports */}
      <ReportsMobileBottomNav />
    </Box>
  );
}

export default CompanyFinanceSummaryList;